import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material'
import { Alert, Box, Button, IconButton, Paper, Skeleton, Stack, Tooltip } from '@mui/material'
import { invalidateDocsAutomationQueries, useDocument, useDocumentsFromCache, useUpdateExtractedData } from '@src/api/document-api'
import { CreditAppLink } from '@src/components/CreditAppLink'
import PageHeader from '@src/components/Layout/PageHeader'
import { StatusChip } from '@src/components/StatusChip'
import { EProcessingStatus } from '@src/types/automationDocument'

import { useUser } from '@src/providers/UserProvider'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import MatchDialog from '../DocsAutomation/components/MatchDialog'
import JSONEditor from './JSONEditor'
import PagesView from './PagesView'

const ViewDocument = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const queryClient = useQueryClient()
  const [documentData, isLoading, error] = useDocument(id)
  const allDocuments = useDocumentsFromCache()

  // Invalidate list cache on unmount so navigating back shows fresh data
  useEffect(() => {
    return () => invalidateDocsAutomationQueries(queryClient)
  }, [queryClient])
  const [searchDocType, setSearchDocType] = useState('')
  const [isJsonEditorExpanded, setIsJsonEditorExpanded] = useState(true)
  const [showLinkDialog, setShowLinkDialog] = useState(false)
  const documentsListUrl = sessionStorage.getItem('documentsListUrl') || '/documents'

  const updateExtractedData = useUpdateExtractedData()

  const handleJsonChange = (newData: Record<string, unknown>) => {
    if (!documentData?.document) return
    updateExtractedData.mutate({
      documentId: documentData.document.id,
      versionTag: documentData.document.versionTag,
      extractedData: newData,
    })
  }

  const handleDocTypeChange = (docType: string) => {
    setSearchDocType(docType)
  }

  const user = useUser()
  const currentUserTag = user ? `a:${user.name.toLocaleLowerCase()}` : null

  // Sort documents: current user's assigned docs first, then unassigned, skip others' assigned docs
  const navigableDocuments = useMemo(() => {
    const myDocs = allDocuments.filter((doc) => doc.tags?.includes(currentUserTag ?? ''))
    const unassignedDocs = allDocuments.filter(
      (doc) => !doc.tags?.some((t) => t.startsWith('a:'))
    )

    // My docs first, then unassigned — skip others' assigned docs
    return [...myDocs, ...unassignedDocs]
  }, [allDocuments, currentUserTag])

  // Navigation entre les documents
  const currentDocIndex = navigableDocuments.findIndex((doc) => doc.id === id)
  const canGoPrevious = currentDocIndex > 0
  const canGoNext = currentDocIndex >= 0 && currentDocIndex < navigableDocuments.length - 1

  const previousDocumentUrl = `/documents/${navigableDocuments[currentDocIndex - 1]?.id}`
  const nextDocumentUrl = `/documents/${navigableDocuments[currentDocIndex + 1]?.id}`

  if (isLoading) {
    return (
      <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Skeleton variant="rectangular" height={40} sx={{ mb: 2, borderRadius: 1 }} />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ flex: 1, minHeight: 0 }}>
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="rectangular" height="100%" sx={{ borderRadius: 1 }} />
          </Box>
          <Skeleton variant="rectangular" height="100%" sx={{ borderRadius: 1, width: { xs: '100%', md: 'min(500px, 40vw)' } }} />
        </Stack>
      </Box>
    )
  }

  if (error || !documentData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              {t('common.reload')}
            </Button>
          }
        >
          {t('common.messages.error')}
        </Alert>
      </Box>
    )
  }

  const { document } = documentData
  const extractedData = document.extractedData || {}
  const senderName = document.email?.sentBy || document.createdByUserFullname
  const formattedDate = new Date(document.createdOn).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
  const breadcrumbLabel = `${senderName} - ${formattedDate}`

  const canEdit =
    document?.processingStatus !== EProcessingStatus.Discarded &&
    document?.processingStatus !== EProcessingStatus.Finalized

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box data-tour="detail-breadcrumbs" sx={{ flex: 1 }}>
          <PageHeader
            breadcrumbs={{ current: breadcrumbLabel, path: [{ label: 'Documents', href: documentsListUrl }] }}
            actions={
              <Stack direction="row" alignItems="center" gap={1}>
                <CreditAppLink creditApp={document.linkedCreditApp} />
                <StatusChip status={document.processingStatus} />
              </Stack>
            }
          />
        </Box>
        <Box data-tour="document-nav" sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t('common.previous')}>
            <span>
              <IconButton
                size="small"
                href={previousDocumentUrl}
                disabled={!canGoPrevious}
                sx={{
                  bgcolor: canGoPrevious ? 'primary.main' : 'action.disabledBackground',
                  color: canGoPrevious ? 'primary.contrastText' : 'action.disabled',
                  '&:hover': {
                    bgcolor: canGoPrevious ? 'primary.dark' : 'action.disabledBackground',
                  },
                }}
              >
                <NavigateBeforeIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('common.next')}>
            <span>
              <IconButton
                size="small"
                href={nextDocumentUrl}
                disabled={!canGoNext}
                sx={{
                  bgcolor: canGoNext ? 'primary.main' : 'action.disabledBackground',
                  color: canGoNext ? 'primary.contrastText' : 'action.disabled',
                  '&:hover': {
                    bgcolor: canGoNext ? 'primary.dark' : 'action.disabledBackground',
                  },
                }}
              >
                <NavigateNextIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        {/* --- COLONNE GAUCHE = PAGES VIEW --- */}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Paper elevation={3} sx={{ p: 2, height: '100%', overflow: 'hidden' }}>
            <PagesView
              pages={documentData.documentPages}
              document={document}
              onDocTypeChange={handleDocTypeChange}
              viewOnly={!canEdit}
              nextDocumentUrl={canGoNext ? nextDocumentUrl : undefined}
              canGoNext={canGoNext}
              onOpenLinkDialog={() => setShowLinkDialog(true)}
            />
          </Paper>
        </Box>

        {/* --- COLONNE DROITE = JSON EDITOR --- */}
        <Box
          data-tour="extracted-data-panel"
          sx={{
            width: isJsonEditorExpanded
              ? { xs: '100%', md: 'min(500px, 40vw)' }
              : { xs: '100%', md: '48px' },
            height: isJsonEditorExpanded
              ? { xs: '40%', md: 'auto' }
              : { xs: 48, md: 'auto' },
            flexShrink: 0,
            transition: 'width 0.3s ease-in-out, height 0.3s ease-in-out',
            position: 'relative',
          }}
        >
          <Paper elevation={3} sx={{ p: 2, height: '100%', overflow: 'hidden', position: 'relative' }}>
            <IconButton
              onClick={() => setIsJsonEditorExpanded(!isJsonEditorExpanded)}
              sx={{
                position: 'absolute',
                left: 8,
                top: 8,
                zIndex: 10,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
              size="small"
            >
              {isJsonEditorExpanded ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
            {isJsonEditorExpanded && (
              <Box sx={{ mt: 5, height: 'calc(100% - 40px)', overflow: 'hidden' }}>
                <JSONEditor
                  data={extractedData}
                  onUpdate={handleJsonChange}
                  error={updateExtractedData.isError ? t('common.messages.error') : null}
                  searchEnabled
                  search={searchDocType}
                  viewOnly={!canEdit}
                />
              </Box>
            )}
          </Paper>
        </Box>
      </Stack>

      <MatchDialog
        docsAutomation={showLinkDialog ? document : null}
        onClose={() => setShowLinkDialog(false)}
      />
    </Box>
  )
}

export default ViewDocument
